import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  AddCircleOutline,
  AttachMoney,
  Delete,
  Edit,
  EmojiEvents,
  FilterList,
  LocalOffer,
  MonetizationOn,
  Refresh,
  ShoppingCart,
  StoreMallDirectory,
  TrendingUp,
  Visibility,
  GetApp,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: theme.palette.background.default,
  },
  mainPaper: {
    flex: 1,
    margin: "0 16px 16px",
    borderRadius: 12,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  },
  tabsBar: {
    background: theme.palette.background.paper,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  tab: {
    fontWeight: 600,
    textTransform: "none",
    fontSize: 14,
    minWidth: 160,
  },
  tabContent: {
    flex: 1,
    overflow: "auto",
    padding: 24,
  },
  kpiRow: {
    display: "flex",
    gap: 16,
    marginBottom: 24,
    flexWrap: "wrap",
  },
  kpiCard: {
    flex: "1 1 180px",
    borderRadius: 12,
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    background: theme.palette.background.paper,
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
  },
  kpiIcon: {
    width: 52,
    height: 52,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiValue: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1,
    marginBottom: 4,
  },
  kpiLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  filtersRow: {
    display: "flex",
    gap: 12,
    marginBottom: 20,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterField: { minWidth: 140 },
  tableContainer: {
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  tableHead: {
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: "12px 16px",
  },
  tableRow: {
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    },
  },
  tableCell: { padding: "12px 16px", fontSize: 14 },
  chip: { fontWeight: 600, fontSize: 11 },
  valueCell: { fontWeight: 700, color: "#2e7d32", fontSize: 15 },
  addBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: 16,
    paddingRight: 16,
  },
  productsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  sectionTitle: { fontWeight: 700, fontSize: 18 },
  emptyState: {
    padding: 60,
    textAlign: "center",
    color: theme.palette.text.secondary,
  },

  // ── Ranking layout ─────────────────────────────────────────────
  rankingLayout: {
    display: "flex",
    gap: 20,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  rankingListCol: {
    flex: "1 1 400px",
    minWidth: 0,
  },
  podiumCol: {
    width: 220,
    flexShrink: 0,
    position: "sticky",
    top: 0,
  },

  // Podium card
  podiumWrap: {
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    background: theme.palette.background.paper,
  },
  podiumHeader: {
    background: "linear-gradient(135deg,#f57f17,#ff8f00)",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  podiumTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  podiumBody: { padding: "12px 0" },
  podiumItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  podiumBadge: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 14,
    flexShrink: 0,
  },
  podiumName: {
    flex: 1,
    fontWeight: 600,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  podiumCount: {
    fontWeight: 700,
    fontSize: 13,
    color: "#2e7d32",
    flexShrink: 0,
  },

  // List rows
  rankingRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 16px",
    borderRadius: 10,
    marginBottom: 8,
    background: theme.palette.background.paper,
    boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 3px 12px rgba(0,0,0,0.1)" },
  },
  rankingPosition: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  rankingName: { flex: 1, fontWeight: 600, fontSize: 14, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rankingStats: { display: "flex", gap: 16, alignItems: "center", flexShrink: 0 },
  rankingStat: { textAlign: "right" },
  rankingStatValue: { fontWeight: 700, fontSize: 14, lineHeight: 1.2 },
  rankingStatLabel: { fontSize: 10, color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.5 },
  viewBtn: {
    borderRadius: 7,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 11,
    padding: "3px 10px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
}));

// ─── Product Dialog ──────────────────────────────────────────────────────────
const ProductDialog = ({ open, onClose, onSave, initial }) => {
  const [form, setForm] = useState({ name: "", type: "product", price: "" });
  useEffect(() => {
    if (open) setForm(initial
      ? { name: initial.name, type: initial.type, price: String(initial.price ?? "") }
      : { name: "", type: "product", price: "" });
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome");
    await onSave(form);
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ fontWeight: 700 }}>{initial ? "Editar Produto/Serviço" : "Novo Produto/Serviço"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12}>
            <TextField label="Nome" fullWidth variant="outlined" value={form.name}
              onChange={(e) => { const v = e.target.value; setForm((p) => ({ ...p, name: v })); }} autoFocus />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Tipo</InputLabel>
              <Select value={form.type} onChange={(e) => { const v = e.target.value; setForm((p) => ({ ...p, type: v })); }} label="Tipo">
                <MenuItem value="product">Produto</MenuItem>
                <MenuItem value="service">Serviço</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Valor padrão (R$)" fullWidth variant="outlined" type="number"
              inputProps={{ step: "0.01", min: "0" }} value={form.price}
              onChange={(e) => { const v = e.target.value; setForm((p) => ({ ...p, price: v })); }}
              helperText="O atendente poderá editar na hora da venda" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} color="default">Cancelar</Button>
        <Button onClick={handleSave} variant="contained" color="primary" style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Agent Products Modal ────────────────────────────────────────────────────
const AgentProductsModal = ({ open, onClose, agent, filters, formatCurrency }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !agent) return;
    setLoading(true);
    api.get("/sales/agent-products", {
      params: {
        userId: agent.userId,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        queueId: filters.queueId !== "all" ? filters.queueId : undefined,
      }
    })
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("Erro ao carregar detalhamento"))
      .finally(() => setLoading(false));
  }, [open, agent, filters]);

  const total = items.reduce((acc, i) => acc + parseFloat(i.totalValue || 0), 0);
  const totalQty = items.reduce((acc, i) => acc + (i.count || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Box>
          <Typography style={{ fontWeight: 700, fontSize: 18 }}>Produtos / Serviços Vendidos</Typography>
          <Typography variant="body2" color="textSecondary">
            {agent?.userName} · {filters?.dateFrom && moment(filters.dateFrom).format("DD/MM/YY")} – {filters?.dateTo && moment(filters.dateTo).format("DD/MM/YY")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box p={4} textAlign="center">
            <ShoppingCart style={{ fontSize: 40, opacity: 0.3, marginBottom: 8 }} />
            <Typography color="textSecondary">Nenhuma venda encontrada no período.</Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" gridGap={12} mb={2} flexWrap="wrap">
              <Chip label={`${totalQty} venda${totalQty !== 1 ? "s" : ""}`} size="small"
                style={{ background: "rgba(25,118,210,0.1)", color: "#1565c0", fontWeight: 600 }} />
              <Chip label={formatCurrency(total)} size="small"
                style={{ background: "rgba(46,125,50,0.1)", color: "#2e7d32", fontWeight: 700 }} />
            </Box>
            <TableContainer style={{ borderRadius: 8, border: "1px solid rgba(0,0,0,0.1)", overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ background: "rgba(0,0,0,0.03)" }}>
                    {["Produto / Serviço", "Tipo", "Qtd", "Total"].map((h, i) => (
                      <TableCell key={h} align={i >= 2 ? "center" : "left"}
                        style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#888" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => (
                    <TableRow key={item.productId ?? "avulsa"} style={{ background: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.01)" }}>
                      <TableCell style={{ padding: "10px 16px", fontSize: 14, fontWeight: 600 }}>
                        <Box display="flex" alignItems="center" gridGap={8}>
                          <LocalOffer style={{ fontSize: 14, opacity: 0.4 }} />
                          {item.productName}
                        </Box>
                      </TableCell>
                      <TableCell style={{ padding: "10px 16px" }}>
                        {item.productType ? (
                          <Chip size="small" label={item.productType === "product" ? "Produto" : "Serviço"}
                            style={{ fontSize: 11, fontWeight: 600,
                              background: item.productType === "product" ? "rgba(25,118,210,0.1)" : "rgba(76,175,80,0.1)",
                              color: item.productType === "product" ? "#1565c0" : "#2e7d32" }} />
                        ) : (
                          <Chip size="small" label="Avulso"
                            style={{ fontSize: 11, fontWeight: 600, background: "rgba(128,128,128,0.1)", color: "#666" }} />
                        )}
                      </TableCell>
                      <TableCell align="center" style={{ padding: "10px 16px", fontWeight: 700 }}>{item.count}</TableCell>
                      <TableCell align="right" style={{ padding: "10px 16px", fontWeight: 700, color: "#2e7d32", fontSize: 15 }}>
                        {formatCurrency(item.totalValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow style={{ background: "rgba(46,125,50,0.05)", borderTop: "2px solid rgba(46,125,50,0.15)" }}>
                    <TableCell colSpan={2} style={{ padding: "10px 16px", fontWeight: 700, fontSize: 14 }}>TOTAL</TableCell>
                    <TableCell align="center" style={{ padding: "10px 16px", fontWeight: 700 }}>{totalQty}</TableCell>
                    <TableCell align="right" style={{ padding: "10px 16px", fontWeight: 700, color: "#2e7d32", fontSize: 16 }}>
                      {formatCurrency(total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Podium colors ────────────────────────────────────────────────────────────
const MEDALS = [
  { emoji: "🥇", bg: "linear-gradient(135deg,#f57f17,#ffa000)", color: "#fff", border: "rgba(245,127,23,0.4)" },
  { emoji: "🥈", bg: "linear-gradient(135deg,#546e7a,#78909c)", color: "#fff", border: "rgba(84,110,122,0.4)" },
  { emoji: "🥉", bg: "linear-gradient(135deg,#bf360c,#e64a19)", color: "#fff", border: "rgba(191,54,12,0.4)" },
];

const ROW_COLORS = [
  { bg: "rgba(255,193,7,0.08)", border: "#f59e0b33" },
  { bg: "rgba(120,144,156,0.08)", border: "#78909c33" },
  { bg: "rgba(191,54,12,0.08)", border: "#bf360c33" },
];

// ─── Sales Podium Component ──────────────────────────────────────────────────
const SalesPodium = ({ ranking, formatCurrency, classes, mode }) => {
  const top3 = ranking.slice(0, 3);
  if (top3.length === 0) return null;

  const getMetricLabel = (agent) =>
    mode === "value"
      ? formatCurrency(agent.totalValue)
      : `${agent.salesCount} venda${agent.salesCount !== 1 ? "s" : ""}`;

  const podiumTitle = mode === "value" ? "Pódio — Maior Valor" : "Pódio — N° Vendas";

  return (
    <div className={classes.podiumWrap}>
      <div className={classes.podiumHeader}
        style={{ background: mode === "value" ? "linear-gradient(135deg,#1b5e20,#2e7d32)" : "linear-gradient(135deg,#f57f17,#ff8f00)" }}>
        <EmojiEvents style={{ color: "#fff", fontSize: 18 }} />
        <Typography className={classes.podiumTitle}>{podiumTitle}</Typography>
      </div>
      <div className={classes.podiumBody}>
        {top3.map((agent, i) => (
          <div key={agent.userId} className={classes.podiumItem}
            style={{ background: ROW_COLORS[i].bg }}>
            <div className={classes.podiumBadge}
              style={{ background: MEDALS[i].bg, color: MEDALS[i].color, fontSize: 16 }}>
              {MEDALS[i].emoji}
            </div>
            <Typography className={classes.podiumName} title={agent.userName}>
              {agent.userName}
            </Typography>
            <Typography className={classes.podiumCount}>
              {getMetricLabel(agent)}
            </Typography>
          </div>
        ))}
        {ranking.length > 3 && (
          <>
            <Divider />
            <Box px={2} pt={1} pb={0.5}>
              <Typography style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                Demais ({ranking.length - 3})
              </Typography>
            </Box>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {ranking.slice(3).map((agent, i) => (
                <div key={agent.userId} className={classes.podiumItem}>
                  <div className={classes.podiumBadge}
                    style={{ background: "rgba(128,128,128,0.1)", color: "#888", fontSize: 12 }}>
                    {i + 4}°
                  </div>
                  <Typography className={classes.podiumName} title={agent.userName}>
                    {agent.userName}
                  </Typography>
                  <Typography className={classes.podiumCount} style={{ color: "#555" }}>
                    {getMetricLabel(agent)}
                  </Typography>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const Vendas = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin" || user.super;
  const [tab, setTab] = useState(0);

  // Products
  const [products, setProducts] = useState([]);
  const [productDialog, setProductDialog] = useState({ open: false, item: null });
  const [deleteProductConfirm, setDeleteProductConfirm] = useState({ open: false, id: null });
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Sales
  const [sales, setSales] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [loadingSales, setLoadingSales] = useState(false);
  const [deleteSaleConfirm, setDeleteSaleConfirm] = useState({ open: false, id: null });
  const [notesModal, setNotesModal] = useState({ open: false, notes: "" });

  // Ranking
  const [ranking, setRanking] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [rankingMode, setRankingMode] = useState("quantity"); // "quantity" | "value"
  const [rankingFilters, setRankingFilters] = useState({
    dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
    dateTo: moment().format("YYYY-MM-DD"),
    queueId: "all",
  });
  const [appliedRankingFilters, setAppliedRankingFilters] = useState({ ...rankingFilters });
  const [agentModal, setAgentModal] = useState({ open: false, agent: null });

  // Filters
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: moment().startOf("month").format("YYYY-MM-DD"),
    dateTo: moment().format("YYYY-MM-DD"),
    userId: "all",
    queueId: "all",
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  useEffect(() => {
    api.get("/sales/filters").then(({ data }) => {
      setUsers(data.users || []);
      setQueues(data.queues || []);
    }).catch(() => {});
  }, []);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try { const { data } = await api.get("/sales/products"); setProducts(data); }
    catch { toast.error("Erro ao carregar produtos"); }
    finally { setLoadingProducts(false); }
  }, []);

  const loadSales = useCallback(async (f = appliedFilters, p = page) => {
    setLoadingSales(true);
    try {
      const { data } = await api.get("/sales", { params: {
        pageNumber: p + 1, dateFrom: f.dateFrom, dateTo: f.dateTo,
        userId: f.userId !== "all" ? f.userId : undefined,
        queueId: f.queueId !== "all" ? f.queueId : undefined,
      }});
      setSales(data.records || []);
      setSalesCount(data.count || 0);
      setTotalCount(data.totalCount || 0);
      setTotalValue(parseFloat(data.totalValue || 0));
    } catch { toast.error("Erro ao carregar vendas"); }
    finally { setLoadingSales(false); }
  }, [appliedFilters, page]);

  const loadRanking = useCallback(async (f = appliedRankingFilters) => {
    setLoadingRanking(true);
    try {
      const { data } = await api.get("/sales/ranking", { params: {
        dateFrom: f.dateFrom, dateTo: f.dateTo,
        queueId: f.queueId !== "all" ? f.queueId : undefined,
      }});
      setRanking(data || []);
    } catch { toast.error("Erro ao carregar ranking"); }
    finally { setLoadingRanking(false); }
  }, [appliedRankingFilters]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadSales(appliedFilters, page); }, [appliedFilters, page]);
  useEffect(() => { loadRanking(appliedRankingFilters); }, [appliedRankingFilters]);

  const handleSaveProduct = async (form) => {
    try {
      if (productDialog.item) { await api.put(`/sales/products/${productDialog.item.id}`, form); toast.success("Produto atualizado"); }
      else { await api.post("/sales/products", form); toast.success("Produto cadastrado"); }
      loadProducts();
    } catch { toast.error("Erro ao salvar produto"); }
  };

  const handleDeleteProduct = async () => {
    try { await api.delete(`/sales/products/${deleteProductConfirm.id}`); toast.success("Produto removido"); loadProducts(); }
    catch { toast.error("Erro ao remover produto"); }
    finally { setDeleteProductConfirm({ open: false, id: null }); }
  };

  const handleDeleteSale = async () => {
    try { await api.delete(`/sales/${deleteSaleConfirm.id}`); toast.success("Venda removida"); loadSales(appliedFilters, page); }
    catch { toast.error("Erro ao remover venda"); }
    finally { setDeleteSaleConfirm({ open: false, id: null }); }
  };

  const formatCurrency = (v) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const avgTicket = totalCount > 0 ? totalValue / totalCount : 0;

  const getDisplayNotes = (s) => {
    if (!s.notes) return "";
    if (s.notes.startsWith("item: ")) {
      const nl = s.notes.indexOf(String.fromCharCode(10));
      return nl >= 0 ? s.notes.slice(nl + 1).trim() : "";
    }
    return s.notes.trim();
  };

  const handleExportSalesExcel = async () => {
    try {
      const { data } = await api.get("/sales", { params: {
        all: "true", dateFrom: appliedFilters.dateFrom, dateTo: appliedFilters.dateTo,
        userId: appliedFilters.userId !== "all" ? appliedFilters.userId : undefined,
        queueId: appliedFilters.queueId !== "all" ? appliedFilters.queueId : undefined,
      }});
      const rows = (data.records || []).map((s) => ({
        "Data": moment(s.createdAt).format("DD/MM/YYYY HH:mm"),
        "Atendente": s.user?.name || "",
        "Cliente": s.clientName || "",
        "Produto / Servico": s.product?.name || (s.notes?.startsWith("item: ") ? s.notes.replace("item: ", "").split(String.fromCharCode(10))[0] : ""),
        "Tipo": s.product ? (s.product.type === "product" ? "Produto" : "Servico") : (s.notes?.startsWith("item: ") ? "Outro" : ""),
        "Valor (R$)": parseFloat(s.value),
        "Observacoes": getDisplayNotes(s),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 18 }, { wch: 22 }, { wch: 28 }, { wch: 28 }, { wch: 12 }, { wch: 14 }, { wch: 40 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Vendas");
      XLSX.writeFile(wb, "vendas_" + appliedFilters.dateFrom + "_" + appliedFilters.dateTo + ".xlsx");
    } catch { toast.error("Erro ao exportar"); }
  };

  const handleExportRankingExcel = () => {
    const rows = displayRanking.map((r, i) => ({
      "Posicao": i + 1, "Atendente": r.userName,
      "N Vendas": r.salesCount, "Total (R$)": parseFloat(r.totalValue),
      "Ticket Medio (R$)": parseFloat(r.avgValue),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 10 }, { wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ranking");
    XLSX.writeFile(wb, "ranking_vendas_" + appliedRankingFilters.dateFrom + "_" + appliedRankingFilters.dateTo + ".xlsx");
  };

  const exportPodiumImage = (mode) => {
    const sorted = [...ranking].sort((a, b) =>
      mode === "value"
        ? parseFloat(b.totalValue) - parseFloat(a.totalValue) || b.salesCount - a.salesCount
        : b.salesCount - a.salesCount || parseFloat(b.totalValue) - parseFloat(a.totalValue)
    );
    if (sorted.length === 0) { toast.warn("Nenhum dado para exportar"); return; }
    const DPR = 2, W = 1080, rowH = 56;
    const bodyRows = Math.max(0, sorted.length - 3);
    const H = 420 + (bodyRows > 0 ? 50 + bodyRows * rowH : 0);
    const canvas = document.createElement("canvas");
    canvas.width = W * DPR; canvas.height = H * DPR;
    const ctx = canvas.getContext("2d"); ctx.scale(DPR, DPR);
    const isValue = mode === "value";
    const grad = ctx.createLinearGradient(0, 0, W, H);
    if (isValue) { grad.addColorStop(0,"#1a3a1a"); grad.addColorStop(0.5,"#1b5e20"); grad.addColorStop(1,"#2e7d32"); }
    else { grad.addColorStop(0,"#3e2723"); grad.addColorStop(0.5,"#bf360c"); grad.addColorStop(1,"#e65100"); }
    ctx.fillStyle = grad; ctx.fillRect(0,0,W,H);
    for (let x=20;x<W;x+=55) for (let y=20;y<H;y+=55) { ctx.beginPath(); ctx.arc(x,y,1.5,0,Math.PI*2); ctx.fillStyle="rgba(255,255,255,0.06)"; ctx.fill(); }
    ctx.fillStyle = isValue ? "rgba(76,175,80,0.5)" : "rgba(255,193,7,0.5)"; ctx.fillRect(0,0,W,6);
    ctx.font="bold 40px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="center"; ctx.textBaseline="middle";
    ctx.fillText(isValue ? "RANKING POR VALOR DA VENDA" : "RANKING POR NUMERO DE VENDAS", W/2, 58);
    ctx.font="18px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.6)";
    ctx.fillText(moment(appliedRankingFilters.dateFrom).format("DD/MM/YYYY") + "  —  " + moment(appliedRankingFilters.dateTo).format("DD/MM/YYYY"), W/2, 100);
    ctx.strokeStyle="rgba(255,255,255,0.15)"; ctx.lineWidth=1; ctx.beginPath(); ctx.moveTo(80,128); ctx.lineTo(W-80,128); ctx.stroke();
    const mColors=[{circle:"#f59e0b",label:"1"},{circle:"#90a4ae",label:"2"},{circle:"#e64a19",label:"3"}];
    const top3=sorted.slice(0,3); const cW=top3.length===1?420:top3.length===2?460:310;
    const gap=20, totalW=top3.length*cW+(top3.length-1)*gap, sX=(W-totalW)/2, cY=148, cH=144;
    const rrp=(x,y,w,h,r)=>{ctx.beginPath();ctx.moveTo(x+r,y);ctx.lineTo(x+w-r,y);ctx.quadraticCurveTo(x+w,y,x+w,y+r);ctx.lineTo(x+w,y+h-r);ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);ctx.lineTo(x+r,y+h);ctx.quadraticCurveTo(x,y+h,x,y+h-r);ctx.lineTo(x,y+r);ctx.quadraticCurveTo(x,y,x+r,y);ctx.closePath();};
    top3.forEach((agent,i)=>{
      const cx=sX+i*(cW+gap), mc=mColors[i];
      ctx.shadowColor="rgba(0,0,0,0.35)"; ctx.shadowBlur=18; ctx.shadowOffsetY=5;
      ctx.fillStyle="rgba(255,255,255,0.13)"; rrp(cx,cY,cW,cH,18); ctx.fill();
      ctx.shadowColor="transparent"; ctx.shadowBlur=0; ctx.shadowOffsetY=0;
      ctx.strokeStyle="rgba(255,255,255,0.22)"; ctx.lineWidth=1.5; rrp(cx,cY,cW,cH,18); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx+46,cY+46,30,0,Math.PI*2); ctx.fillStyle=mc.circle; ctx.fill();
      ctx.font="bold 20px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="center"; ctx.textBaseline="middle";
      ctx.fillText(mc.label+"º",cx+46,cY+46);
      const mxLen=Math.floor(cW/13), nm=agent.userName.length>mxLen?agent.userName.substring(0,mxLen-1)+"…":agent.userName;
      ctx.font="bold 19px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="left"; ctx.textBaseline="middle";
      ctx.fillText(nm,cx+88,cY+36);
      ctx.font="bold 24px Arial,sans-serif"; ctx.fillStyle=isValue?"#a5d6a7":"#ffe082";
      ctx.fillText(isValue?formatCurrency(agent.totalValue):agent.salesCount+" vendas",cx+88,cY+68);
      ctx.font="14px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.55)";
      ctx.fillText(isValue?agent.salesCount+" venda"+(agent.salesCount!==1?"s":""):"Total: "+formatCurrency(agent.totalValue),cx+88,cY+96);
      ctx.font="13px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.38)";
      ctx.fillText("Ticket médio: "+formatCurrency(agent.avgValue),cx+88,cY+118);
    });
    if (sorted.length>3) {
      const lY=cY+cH+42;
      ctx.font="bold 12px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.38)"; ctx.textAlign="left"; ctx.textBaseline="middle";
      ctx.fillText("DEMAIS POSIÇÕES",80,lY-16);
      sorted.slice(3).forEach((agent,i)=>{
        const ry=lY+i*rowH;
        ctx.fillStyle=i%2===0?"rgba(255,255,255,0.07)":"rgba(255,255,255,0.04)"; rrp(60,ry,W-120,rowH-8,10); ctx.fill();
        ctx.font="bold 14px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.5)"; ctx.textAlign="center";
        ctx.fillText((i+4)+"º",100,ry+(rowH-8)/2);
        const n36=agent.userName.length>36?agent.userName.substring(0,35)+"…":agent.userName;
        ctx.font="15px Arial,sans-serif"; ctx.fillStyle="#fff"; ctx.textAlign="left";
        ctx.fillText(n36,130,ry+(rowH-8)/2);
        ctx.font="bold 15px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.85)"; ctx.textAlign="right";
        ctx.fillText(isValue?formatCurrency(agent.totalValue):agent.salesCount+" vendas",W-80,ry+(rowH-8)/2);
      });
    }
    ctx.font="13px Arial,sans-serif"; ctx.fillStyle="rgba(255,255,255,0.28)"; ctx.textAlign="center"; ctx.textBaseline="middle";
    ctx.fillText("Estratiza Hub",W/2,H-22);
    const link=document.createElement("a");
    link.download="podio_"+(isValue?"valor":"vendas")+"_"+appliedRankingFilters.dateFrom+".png";
    link.href=canvas.toDataURL("image/png"); link.click();
  };

  // Sort ranking by active mode
  const displayRanking = [...ranking].sort((a, b) =>
    rankingMode === "value"
      ? parseFloat(b.totalValue) - parseFloat(a.totalValue) || b.salesCount - a.salesCount
      : b.salesCount - a.salesCount || parseFloat(b.totalValue) - parseFloat(a.totalValue)
  );

  const rankingTotal = displayRanking.reduce((acc, r) => acc + parseFloat(r.totalValue || 0), 0);
  const rankingTotalQty = displayRanking.reduce((acc, r) => acc + (r.salesCount || 0), 0);

  return (
    <div className={classes.root}>
      <MainHeader><Title>Vendas</Title></MainHeader>

      <Paper className={classes.mainPaper} elevation={0}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} className={classes.tabsBar} indicatorColor="primary" textColor="primary">
          <Tab className={classes.tab} label={<Box display="flex" alignItems="center" gridGap={8}><StoreMallDirectory style={{ fontSize: 18 }} />Produtos / Serviços</Box>} />
          <Tab className={classes.tab} label={<Box display="flex" alignItems="center" gridGap={8}><ShoppingCart style={{ fontSize: 18 }} />Vendas Gerais</Box>} />
          <Tab className={classes.tab} label={
            <Box display="flex" alignItems="center" gridGap={8}>
              <EmojiEvents style={{ fontSize: 18, flexShrink: 0 }} />
              <Box textAlign="left">
                <div style={{ lineHeight: 1.2 }}>Ranking de Vendas</div>
                <div style={{ fontSize: 11, opacity: 0.65, lineHeight: 1.2, fontWeight: 500 }}>e Detalhamento</div>
              </Box>
            </Box>
          } />
        </Tabs>

        {/* ── TAB 0 — Products ─────────────────────────────────────── */}
        {tab === 0 && (
          <div className={classes.tabContent}>
            <div className={classes.productsHeader}>
              <Typography className={classes.sectionTitle}>Catálogo de Produtos e Serviços</Typography>
              {isAdmin && (
                <Button variant="contained" color="primary" startIcon={<AddCircleOutline />} className={classes.addBtn}
                  onClick={() => setProductDialog({ open: true, item: null })}>Novo</Button>
              )}
            </div>
            {loadingProducts ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : products.length === 0 ? (
              <div className={classes.emptyState}>
                <StoreMallDirectory style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>Nenhum produto cadastrado</Typography>
                {isAdmin && <Typography variant="body2">Clique em "Novo" para cadastrar um produto ou serviço.</Typography>}
              </div>
            ) : (
              <TableContainer className={classes.tableContainer}>
                <Table size="small">
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                      <TableCell className={classes.tableHeadCell}>Tipo</TableCell>
                      <TableCell className={classes.tableHeadCell}>Valor Padrão</TableCell>
                      {isAdmin && <TableCell className={classes.tableHeadCell} align="right">Ações</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((p) => (
                      <TableRow key={p.id} className={classes.tableRow}>
                        <TableCell className={classes.tableCell}>
                          <Box display="flex" alignItems="center" gridGap={8}><LocalOffer style={{ fontSize: 16, opacity: 0.5 }} /><strong>{p.name}</strong></Box>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Chip size="small" label={p.type === "product" ? "Produto" : "Serviço"} className={classes.chip}
                            style={{ background: p.type === "product" ? "rgba(25,118,210,0.1)" : "rgba(76,175,80,0.1)", color: p.type === "product" ? "#1565c0" : "#2e7d32" }} />
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <span style={{ fontWeight: 600, color: "#2e7d32" }}>{p.price > 0 ? formatCurrency(p.price) : "—"}</span>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className={classes.tableCell} align="right">
                            <Tooltip title="Editar"><IconButton size="small" onClick={() => setProductDialog({ open: true, item: p })}><Edit fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Remover"><IconButton size="small" style={{ color: "#e53935" }} onClick={() => setDeleteProductConfirm({ open: true, id: p.id })}><Delete fontSize="small" /></IconButton></Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}

        {/* ── TAB 1 — Sales ────────────────────────────────────────── */}
        {tab === 1 && (
          <div className={classes.tabContent}>
            <div className={classes.kpiRow}>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(25,118,210,0.1)" }}><ShoppingCart style={{ color: "#1976d2", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue}>{totalCount}</Typography><Typography className={classes.kpiLabel}>Total de Vendas</Typography></div>
              </Paper>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(46,125,50,0.1)" }}><AttachMoney style={{ color: "#2e7d32", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue} style={{ color: "#2e7d32" }}>{formatCurrency(totalValue)}</Typography><Typography className={classes.kpiLabel}>Valor Total</Typography></div>
              </Paper>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(230,81,0,0.1)" }}><TrendingUp style={{ color: "#e65100", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue} style={{ color: "#e65100" }}>{formatCurrency(avgTicket)}</Typography><Typography className={classes.kpiLabel}>Ticket Médio</Typography></div>
              </Paper>
            </div>

            <Box style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between" }}>
              <Box style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <TextField label="De" type="date" variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }}
                  value={filters.dateFrom} onChange={(e) => { const v = e.target.value; setFilters((p) => ({ ...p, dateFrom: v })); }} />
                <TextField label="Até" type="date" variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }}
                  value={filters.dateTo} onChange={(e) => { const v = e.target.value; setFilters((p) => ({ ...p, dateTo: v })); }} />
                <FormControl variant="outlined" size="small" className={classes.filterField} style={{ minWidth: 160 }}>
                  <InputLabel>Atendente</InputLabel>
                  <Select value={filters.userId} onChange={(e) => { const v = e.target.value; setFilters((p) => ({ ...p, userId: v })); }} label="Atendente">
                    <MenuItem value="all">Todos</MenuItem>
                    {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" className={classes.filterField} style={{ minWidth: 160 }}>
                  <InputLabel>Fila</InputLabel>
                  <Select value={filters.queueId} onChange={(e) => { const v = e.target.value; setFilters((p) => ({ ...p, queueId: v })); }} label="Fila">
                    <MenuItem value="all">Todas</MenuItem>
                    {queues.map((q) => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" color="primary" startIcon={<FilterList />} className={classes.addBtn}
                  onClick={() => { setPage(0); setAppliedFilters({ ...filters }); }}>Filtrar</Button>
                <Tooltip title="Atualizar"><IconButton onClick={() => loadSales(appliedFilters, page)} size="small"><Refresh /></IconButton></Tooltip>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 2 }}>
                <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExportSalesExcel}
                  style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}>
                  Exportar Excel
                </Button>
              </Box>
            </Box>

            {loadingSales ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : sales.length === 0 ? (
              <div className={classes.emptyState}>
                <MonetizationOn style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>Nenhuma venda no período</Typography>
                <Typography variant="body2">Ajuste os filtros ou registre uma venda pelo chat.</Typography>
              </div>
            ) : (
              <>
                <TableContainer className={classes.tableContainer}>
                  <Table size="small">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell}>Data</TableCell>
                        <TableCell className={classes.tableHeadCell}>Atendente</TableCell>
                        <TableCell className={classes.tableHeadCell}>Cliente</TableCell>
                        <TableCell className={classes.tableHeadCell}>Produto / Serviço</TableCell>
                        <TableCell className={classes.tableHeadCell}>Tipo</TableCell>
                        <TableCell className={classes.tableHeadCell} align="right">Valor</TableCell>
                        <TableCell className={classes.tableHeadCell} align="center">Observações</TableCell>
                        {isAdmin && <TableCell className={classes.tableHeadCell} align="right">Ações</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.map((s) => (
                        <TableRow key={s.id} className={classes.tableRow}>
                          <TableCell className={classes.tableCell} style={{ whiteSpace: "nowrap" }}>{moment(s.createdAt).format("DD/MM/YY HH:mm")}</TableCell>
                          <TableCell className={classes.tableCell}>{s.user?.name || "—"}</TableCell>
                          <TableCell className={classes.tableCell}>{s.clientName}</TableCell>
                          <TableCell className={classes.tableCell}>{s.product?.name || (s.notes && s.notes.startsWith("item: ") ? s.notes.replace("item: ", "").split("\n")[0] : "—")}</TableCell>
                          <TableCell className={classes.tableCell}>
                            {s.product ? (
                              <Chip size="small" label={s.product.type === "product" ? "Produto" : "Serviço"} className={classes.chip}
                                style={{ background: s.product.type === "product" ? "rgba(25,118,210,0.1)" : "rgba(76,175,80,0.1)", color: s.product.type === "product" ? "#1565c0" : "#2e7d32" }} />
                            ) : s.notes?.startsWith("item: ") ? (
                              <Chip size="small" label="Outro" className={classes.chip}
                                style={{ background: "rgba(103,58,183,0.1)", color: "#4527a0" }} />
                            ) : "—"}
                          </TableCell>
                          <TableCell className={classes.tableCell} align="right"><span className={classes.valueCell}>{formatCurrency(s.value)}</span></TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {(() => {
                              const obs = getDisplayNotes(s);
                              if (!obs) return <span style={{ color: "#bbb" }}>—</span>;
                              return (
                                <Tooltip title={obs.length > 80 ? obs.substring(0, 80) + "..." : obs}>
                                  <IconButton size="small" onClick={() => setNotesModal({ open: true, notes: obs })}>
                                    <Visibility fontSize="small" style={{ color: "#1976d2" }} />
                                  </IconButton>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          {isAdmin && (
                            <TableCell className={classes.tableCell} align="right">
                              <Tooltip title="Remover venda"><IconButton size="small" style={{ color: "#e53935" }} onClick={() => setDeleteSaleConfirm({ open: true, id: s.id })}><Delete fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination component="div" count={salesCount} page={page}
                  onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} rowsPerPageOptions={[20]}
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count} vendas`} />
              </>
            )}
          </div>
        )}

        {/* ── TAB 2 — Ranking de Vendas ────────────────────────────── */}
        {tab === 2 && (
          <div className={classes.tabContent}>
            {/* KPI cards */}
            <div className={classes.kpiRow}>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(255,193,7,0.12)" }}><EmojiEvents style={{ color: "#f59e0b", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue}>{displayRanking.length}</Typography><Typography className={classes.kpiLabel}>Atendentes com Vendas</Typography></div>
              </Paper>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(46,125,50,0.1)" }}><AttachMoney style={{ color: "#2e7d32", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue} style={{ color: "#2e7d32" }}>{formatCurrency(rankingTotal)}</Typography><Typography className={classes.kpiLabel}>Total Geral do Período</Typography></div>
              </Paper>
              <Paper className={classes.kpiCard} elevation={0}>
                <Box className={classes.kpiIcon} style={{ background: "rgba(25,118,210,0.1)" }}><ShoppingCart style={{ color: "#1976d2", fontSize: 26 }} /></Box>
                <div><Typography className={classes.kpiValue}>{rankingTotalQty}</Typography><Typography className={classes.kpiLabel}>Vendas no Período</Typography></div>
              </Paper>
            </div>

            {/* ── Mode toggle ── */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gridGap={12} mb={2}>
              <Box>
                <Box display="flex" style={{ borderRadius: 10, overflow: "hidden", border: "1.5px solid", borderColor: "rgba(0,0,0,0.12)", width: "fit-content" }}>
                  {[
                    { key: "quantity", label: "Ranking por Número de Vendas", icon: "🏅" },
                    { key: "value",    label: "Ranking por Valor da Venda",   icon: "💰" },
                  ].map((opt) => (
                    <button key={opt.key} onClick={() => setRankingMode(opt.key)}
                      style={{
                        padding: "8px 18px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
                        display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                        background: rankingMode === opt.key ? "#4caf50" : "transparent",
                        color: rankingMode === opt.key ? "#fff" : "inherit",
                        transition: "all 0.2s",
                      }}>
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                </Box>
                <Box style={{
                  marginTop: 8, maxWidth: 520, padding: "10px 14px", borderRadius: 8,
                  background: rankingMode === "quantity" ? "rgba(76,175,80,0.08)" : "rgba(25,118,210,0.08)",
                  border: `1px solid ${rankingMode === "quantity" ? "rgba(76,175,80,0.25)" : "rgba(25,118,210,0.25)"}`,
                }}>
                  <Typography style={{ fontSize: 13, fontWeight: 700, marginBottom: 4,
                    color: rankingMode === "quantity" ? "#2e7d32" : "#1565c0" }}>
                    {rankingMode === "quantity"
                      ? "Quem fechou mais vendas, independente do valor de cada uma."
                      : "Quem gerou mais receita no total, independente de quantas vendas fez."}
                  </Typography>
                  <Typography style={{ fontSize: 12, color: "#666", lineHeight: 1.6 }}>
                    {rankingMode === "quantity"
                      ? "Ex: João tem 5 vendas e Maria tem 2 — João está na frente porque tem mais vendas, mesmo que o valor de cada uma seja menor."
                      : "Ex: João tem 10 vendas somando R$5.000 e Maria tem 1 venda de R$10.000 — Maria está na frente porque seu valor total acumulado é maior."}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gridGap={8} alignItems="flex-end" style={{ paddingTop: 4 }}>
                <Typography variant="caption" style={{ color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Exportar Pódio em Imagem</Typography>
                <Box display="flex" gridGap={8}>
                  <Button variant="contained" size="small" onClick={() => exportPodiumImage("quantity")}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg,#e65100,#f57f17)", color: "#fff" }}>
                    🏅 Pódio — Nº de Vendas
                  </Button>
                  <Button variant="contained" size="small" onClick={() => exportPodiumImage("value")}
                    style={{ borderRadius: 8, textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg,#1b5e20,#2e7d32)", color: "#fff" }}>
                    💰 Pódio — Por Valor
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Filters */}
            <Box style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between" }}>
              <Box style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
                <TextField label="De" type="date" variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }}
                  value={rankingFilters.dateFrom} onChange={(e) => { const v = e.target.value; setRankingFilters((p) => ({ ...p, dateFrom: v })); }} />
                <TextField label="Até" type="date" variant="outlined" size="small" className={classes.filterField} InputLabelProps={{ shrink: true }}
                  value={rankingFilters.dateTo} onChange={(e) => { const v = e.target.value; setRankingFilters((p) => ({ ...p, dateTo: v })); }} />
                <FormControl variant="outlined" size="small" className={classes.filterField} style={{ minWidth: 160 }}>
                  <InputLabel>Fila</InputLabel>
                  <Select value={rankingFilters.queueId} onChange={(e) => { const v = e.target.value; setRankingFilters((p) => ({ ...p, queueId: v })); }} label="Fila">
                    <MenuItem value="all">Todas</MenuItem>
                    {queues.map((q) => <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" color="primary" startIcon={<FilterList />} className={classes.addBtn}
                  onClick={() => setAppliedRankingFilters({ ...rankingFilters })}>Filtrar</Button>
                <Tooltip title="Atualizar"><IconButton onClick={() => loadRanking(appliedRankingFilters)} size="small"><Refresh /></IconButton></Tooltip>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 2 }}>
                <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExportRankingExcel}
                  style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}>
                  Exportar Excel
                </Button>
              </Box>
            </Box>

            {loadingRanking ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : displayRanking.length === 0 ? (
              <div className={classes.emptyState}>
                <EmojiEvents style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>Nenhuma venda no período</Typography>
                <Typography variant="body2">Ajuste o período ou registre vendas pelo chat.</Typography>
              </div>
            ) : (
              <div className={classes.rankingLayout}>
                {/* List col */}
                <div className={classes.rankingListCol}>
                  {displayRanking.map((agent, index) => {
                    const medal = index < 3 ? MEDALS[index] : null;
                    const rowColor = index < 3 ? ROW_COLORS[index] : null;
                    const barRef = rankingMode === "value"
                      ? parseFloat(displayRanking[0]?.totalValue || 1)
                      : (displayRanking[0]?.salesCount || 1);
                    const barVal = rankingMode === "value"
                      ? parseFloat(agent.totalValue)
                      : agent.salesCount;
                    const barWidth = Math.max(4, (barVal / barRef) * 100);

                    return (
                      <div key={agent.userId} className={classes.rankingRow}
                        style={rowColor ? { border: `1.5px solid ${rowColor.border}`, background: rowColor.bg } : {}}>
                        {/* Position badge */}
                        <div className={classes.rankingPosition}
                          style={medal ? { background: medal.bg, color: medal.color } : { background: "rgba(128,128,128,0.1)", color: "#888", fontSize: 12 }}>
                          {medal ? <span style={{ fontSize: 16 }}>{medal.emoji}</span> : `${index + 1}°`}
                        </div>

                        {/* Name + bar */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.rankingName}>{agent.userName}</Typography>
                          <div style={{ marginTop: 5, height: 5, borderRadius: 3, background: "rgba(128,128,128,0.12)", overflow: "hidden" }}>
                            <div style={{
                              height: "100%", width: `${barWidth}%`, borderRadius: 3, transition: "width 0.6s ease",
                              background: index === 0 ? "linear-gradient(90deg,#f59e0b,#fbbf24)"
                                : index === 1 ? "linear-gradient(90deg,#78909c,#b0bec5)"
                                : index === 2 ? "linear-gradient(90deg,#bf360c,#e64a19)"
                                : "linear-gradient(90deg,#4caf50,#81c784)",
                            }} />
                          </div>
                        </div>

                        {/* Stats — highlight active metric */}
                        <div className={classes.rankingStats}>
                          <div className={classes.rankingStat}>
                            <Typography className={classes.rankingStatValue}
                              style={rankingMode === "quantity" ? { color: "#1976d2", fontSize: 16 } : {}}>
                              {agent.salesCount}
                            </Typography>
                            <Typography className={classes.rankingStatLabel}>Vendas</Typography>
                          </div>
                          <div className={classes.rankingStat}>
                            <Typography className={classes.rankingStatValue}
                              style={{ color: rankingMode === "value" ? "#2e7d32" : undefined, fontSize: rankingMode === "value" ? 16 : 14 }}>
                              {formatCurrency(agent.totalValue)}
                            </Typography>
                            <Typography className={classes.rankingStatLabel}>Total</Typography>
                          </div>
                          <div className={classes.rankingStat}>
                            <Typography className={classes.rankingStatValue} style={{ color: "#666", fontSize: 12 }}>{formatCurrency(agent.avgValue)}</Typography>
                            <Typography className={classes.rankingStatLabel}>Ticket Médio</Typography>
                          </div>
                          <Button variant="outlined" size="small" startIcon={<Visibility style={{ fontSize: 14 }} />}
                            className={classes.viewBtn} onClick={() => setAgentModal({ open: true, agent })}>
                            Ver Produtos
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Podium col */}
                <div className={classes.podiumCol}>
                  <SalesPodium ranking={displayRanking} formatCurrency={formatCurrency} classes={classes} mode={rankingMode} />
                </div>
              </div>
            )}
          </div>
        )}
      </Paper>

      {/* Dialogs */}
      <ProductDialog open={productDialog.open} onClose={() => setProductDialog({ open: false, item: null })} onSave={handleSaveProduct} initial={productDialog.item} />
      <AgentProductsModal open={agentModal.open} onClose={() => setAgentModal({ open: false, agent: null })} agent={agentModal.agent} filters={appliedRankingFilters} formatCurrency={formatCurrency} />
      <ConfirmationModal title="Remover Produto" open={deleteProductConfirm.open} onClose={() => setDeleteProductConfirm({ open: false, id: null })} onConfirm={handleDeleteProduct}>
        Tem certeza que deseja remover este produto? Vendas registradas não serão afetadas.
      </ConfirmationModal>
      <ConfirmationModal title="Remover Venda" open={deleteSaleConfirm.open} onClose={() => setDeleteSaleConfirm({ open: false, id: null })} onConfirm={handleDeleteSale}>
        Tem certeza que deseja remover esta venda? Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <Dialog open={notesModal.open} onClose={() => setNotesModal({ open: false, notes: "" })} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700 }}>Observações da Venda</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: "pre-wrap", fontSize: 15, lineHeight: 1.7 }}>{notesModal.notes}</Typography>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={() => setNotesModal({ open: false, notes: "" })} variant="outlined" style={{ borderRadius: 8, textTransform: "none", fontWeight: 600 }}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Vendas;
